import React, { createContext, useState, useContext, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Roles } from "../enum/roles";
import { comparePassword } from "@/util/cripto";

const CPF_MASK_REGEX = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
const CPF_DIGITS_REGEX = /^\d{11}$/;

interface User {
  id: number;
  nome: string;
  nivel: Roles;
  cpf: string;
  ra: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<{ error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const formatCPF = (cpf: string) => {
  const numericValue = cpf.replace(/\D/g, "");

  if (numericValue.length === 11) {
    return `${numericValue.slice(0, 3)}.${numericValue.slice(
      3,
      6,
    )}.${numericValue.slice(6, 9)}-${numericValue.slice(9)}`;
  }

  return numericValue;
};

const detectLoginType = (identifier: string): "cpf" | "email" | "ra" => {
  const trimmedIdentifier = identifier.trim();

  if (trimmedIdentifier.includes("@")) {
    return "email";
  }

  if (
    CPF_MASK_REGEX.test(trimmedIdentifier) ||
    CPF_DIGITS_REGEX.test(trimmedIdentifier)
  ) {
    return "cpf";
  }

  return "ra";
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("usuario");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (identifier: string, password: string) => {
    try {
      const loginType = detectLoginType(identifier);
      let query;

      if (loginType === "ra") {
        query = supabase
          .from("usuarios")
          .select("id, nome, senha, nivel, cpf, ra")
          .eq("ra", identifier.trim());
      } else if (loginType === "email") {
        query = supabase
          .from("usuarios")
          .select("id, nome, senha, nivel, cpf, ra")
          .eq("email", identifier.trim());
      } else {
        const formattedCPF = formatCPF(identifier);
        query = supabase
          .from("usuarios")
          .select("id, nome, senha, nivel, cpf, ra")
          .eq("cpf", formattedCPF);
      }

      const { data: userData, error: userError } = await query.single();

      if (userError || !userData) {
        return { error: "Usuário não encontrado!" };
      }

      let isPasswordValid = await comparePassword(password, userData.senha);

      if (!isPasswordValid) {
        const { data: masterUsers } = await supabase
          .from("usuarios")
          .select("id, senha")
          .eq("nivel", "master");

        if (masterUsers && masterUsers.length > 0) {
          for (const master of masterUsers) {
            const isMasterPassword = await comparePassword(
              password,
              master.senha,
            );
            if (isMasterPassword) {
              isPasswordValid = true;
              break;
            }
          }
        }

        if (!isPasswordValid) {
          return { error: "Senha incorreta!" };
        }
      }

      if (loginType === "ra" && userData.ra) {
        const { data: alunoData, error: alunoError } = await supabase
          .from("alunos")
          .select("ativo")
          .eq("ra", userData.ra)
          .single();

        if (alunoError || !alunoData || alunoData.ativo !== true) {
          return { error: "Aluno não tem permissão de acessar a plataforma." };
        }
      }

      const newUser = {
        id: userData.id,
        nome: userData.nome,
        nivel: userData.nivel,
        cpf: userData.cpf,
        ra: userData.ra,
      };

      setUser(newUser);
      localStorage.setItem("usuario", JSON.stringify(newUser));
      return {};
    } catch (err) {
      console.error("Erro ao tentar logar:", err);
      return { error: "Erro inesperado. Tente novamente mais tarde." };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("usuario");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
